import { z } from "zod";

// Schémas partagés entre l'API, l'interface web et, plus tard, l'app mobile.

export const REGISTRATION_MODES = ["open", "invite", "closed"] as const;
export type RegistrationMode = (typeof REGISTRATION_MODES)[number];

export const LOCALES = ["en", "fr", "nl", "de", "es", "it"] as const;
export const DEFAULT_LOCALE: Locale = "en";
export type Locale = (typeof LOCALES)[number];

export const ROLES = ["admin", "user"] as const;
export type Role = (typeof ROLES)[number];

export const emailSchema = z.string().trim().toLowerCase().max(254).pipe(z.email());
export const passwordSchema = z.string().min(10).max(200);
export const nameSchema = z.string().trim().min(1).max(100);
export const timezoneSchema = z.string().refine(isValidTimezone, "Unknown time zone");
export const tokenSchema = z.string().min(20).max(200);

export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const signupSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema,
  locale: z.enum(LOCALES).default("en"),
  invitationToken: tokenSchema.optional(),
});
export type SignupInput = z.input<typeof signupSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
  remember: z.boolean().default(false),
});
export type LoginInput = z.input<typeof loginSchema>;

/** Deuxième étape de connexion quand la double authentification est active. */
export const loginTotpSchema = z.object({
  challenge: z.string().min(20).max(100),
  code: z.string().trim().min(6).max(20),
  remember: z.boolean().default(false),
});
export const totpCodeSchema = z.object({ code: z.string().trim().min(6).max(20) });
export const passwordConfirmSchema = z.object({ password: z.string().min(1).max(200) });

export const START_VIEWS = ["week", "day", "threeDays", "month", "agenda", "boxes"] as const;
export const searchQuerySchema = z.object({ q: z.string().trim().min(2).max(100) });
export const userPrefsSchema = z
  .object({
    startView: z.enum(START_VIEWS),
    timeFormat: z.enum(["auto", "12", "24"]),
    weekNumbers: z.boolean(),
    showBlocks: z.boolean(),
    theme: z.enum(["system", "light", "dark"]),
    notify: z.object({ bookings: z.boolean(), shares: z.boolean(), reconnect: z.boolean(), dailyRecap: z.boolean() }).partial(),
  })
  .partial();
export type UserPrefs = z.infer<typeof userPrefsSchema>;

export interface SessionView {
  id: string;
  current: boolean;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  lastSeenAt: string | null;
}
export interface SecurityView {
  totpEnabled: boolean;
  backupCodesLeft: number;
  sessions: SessionView[];
}

export const tokenOnlySchema = z.object({ token: tokenSchema });

export const forgotPasswordSchema = z.object({ email: emailSchema });

export const resetPasswordSchema = z.object({ token: tokenSchema, password: passwordSchema });

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
});

export const updateProfileSchema = z
  .object({
    name: nameSchema,
    timezone: timezoneSchema,
    locale: z.enum(LOCALES),
    weekStart: z.union([z.literal(0), z.literal(1), z.literal(6)]),
    home: z
      .object({ name: z.string().trim().min(1).max(300), lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180) })
      .nullable(),
    prefs: z.lazy(() => userPrefsSchema),
  })
  .partial();
export type UpdateProfileInput = z.input<typeof updateProfileSchema>;

export const mailSettingsSchema = z.object({
  fromEmail: emailSchema,
  fromName: z.string().trim().min(1).max(100),
  // Absente = on garde la clé déjà enregistrée.
  brevoApiKey: z.string().trim().min(10).max(500).optional(),
});

export const setupSchema = z.object({
  token: z.string().min(1).max(200),
  admin: z.object({ name: nameSchema, email: emailSchema, password: passwordSchema }),
  instance: z.object({
    registrationMode: z.enum(REGISTRATION_MODES).default("invite"),
    defaultTimezone: timezoneSchema.default("Europe/Paris"),
    mail: mailSettingsSchema,
  }),
});
export type SetupInput = z.input<typeof setupSchema>;

export const instanceSettingsSchema = z
  .object({
    registrationMode: z.enum(REGISTRATION_MODES),
    defaultTimezone: timezoneSchema,
    mail: mailSettingsSchema,
    google: z.lazy(() => googleAppSchema),
    microsoft: z.lazy(() => microsoftAppSchema),
    /** Clé Google Places ; chaîne vide = retirer la clé. */
    placesApiKey: z.string().trim().max(200),
    /** Pages légales : textes à null = textes générés. */
    legal: z
      .object({
        operator: z.string().trim().max(200).nullable(),
        contact: z.string().trim().max(254).nullable(),
        privacyText: z.string().max(50_000).nullable(),
        termsText: z.string().max(50_000).nullable(),
      })
      .partial(),
  })
  .partial();
export type InstanceSettingsInput = z.input<typeof instanceSettingsSchema>;

export const invitationSchema = z.object({ email: emailSchema });

export interface PublicUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  timezone: string;
  locale: Locale;
  weekStart: number;
  /** Ville par défaut, pour classer les suggestions de lieux. */
  home: { name: string; lat: number; lon: number } | null;
  prefs: UserPrefs;
}

export interface InstanceSettingsView {
  registrationMode: RegistrationMode;
  defaultTimezone: string;
  mail: { fromEmail: string | null; fromName: string | null; brevoConfigured: boolean };
  google: { clientId: string | null; secretConfigured: boolean; redirectUri: string };
  microsoft: { clientId: string | null; secretConfigured: boolean; redirectUri: string };
  placesConfigured: boolean;
  legal: { operator: string | null; contact: string | null; privacyCustom: boolean; termsCustom: boolean };
}

export interface LegalView {
  kind: "privacy" | "terms";
  text: string;
  custom: boolean;
}

export interface StatusView {
  version: string;
  startedAt: string;
  database: boolean;
  mailConfigured: boolean;
  pushEnabled: boolean;
  oauth: { google: boolean; microsoft: boolean };
  users: number;
  connections: { total: number; ok: number; error: number; reauth: number; syncing: number };
  failing: { userEmail: string; provider: string; label: string; status: string; lastError: string | null; since: string | null }[];
  backups: { file: string; size: number; date: string }[] | null;
  errors: { at: string; source: string; message: string }[];
}

export interface SetupStatus {
  completed: boolean;
  registrationMode: RegistrationMode;
}

export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: "active" | "disabled";
  emailVerified: boolean;
  createdAt: string;
}

/** Codes d'erreur stables renvoyés par l'API ; l'interface les traduit. */
export const ERROR_CODES = [
  "VALIDATION",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "INVALID_CREDENTIALS",
  "EMAIL_NOT_VERIFIED",
  "ACCOUNT_DISABLED",
  "EMAIL_TAKEN",
  "REGISTRATION_CLOSED",
  "INVITATION_REQUIRED",
  "INVALID_TOKEN",
  "SETUP_DONE",
  "SETUP_TOKEN_INVALID",
  "SETUP_TOKEN_MISSING",
  "MAIL_NOT_CONFIGURED",
  "MAIL_FAILED",
  "CSRF",
  "PROVIDER_NOT_CONFIGURED",
  "PROVIDER_AUTH",
  "PROVIDER_UNREACHABLE",
  "PROVIDER_INVALID",
  "PROVIDER_CONFLICT",
  "READ_ONLY",
  "EVENT_REJECTED",
  "SHARE_EXISTS",
  "SLUG_TAKEN",
  "SLOT_TAKEN",
  "TOTP_INVALID",
  "RATE_LIMITED",
  "INTERNAL",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiErrorBody {
  error: { code: ErrorCode; message: string; fields?: Record<string, string> };
}

/** En-tête exigé sur toute requête qui modifie quelque chose (protection CSRF). */
export const CSRF_HEADER = "x-requested-with";
export const CSRF_VALUE = "confluence";

// ───────────── Jalon J1 : sources d'agendas ─────────────

export const PROVIDERS = ["google", "microsoft", "caldav", "ics"] as const;
export type Provider = (typeof PROVIDERS)[number];
export const VISIBILITY_LEVELS = ["hidden", "busy", "details"] as const;
export type VisibilityLevel = (typeof VISIBILITY_LEVELS)[number];

export const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Color must be #RRGGBB");

export const caldavConnectSchema = z.object({
  serverUrl: z.url({ protocol: /^https?$/ }).max(500),
  username: z.string().trim().min(1).max(200),
  password: z.string().min(1).max(500),
});

export const icsSubscribeSchema = z.object({
  url: z
    .string()
    .trim()
    .max(2000)
    .transform((u) => u.replace(/^webcals?:\/\//i, "https://"))
    .pipe(z.url({ protocol: /^https?$/ })),
  name: z.string().trim().min(1).max(100),
});

export const calendarPatchSchema = z
  .object({
    displayName: z.string().trim().min(1).max(100).nullable(),
    color: hexColorSchema,
    shown: z.boolean(),
    countsAsBusy: z.boolean(),
    defaultVisibility: z.enum(VISIBILITY_LEVELS),
    isDefault: z.literal(true),
  })
  .partial();
export type CalendarPatch = z.input<typeof calendarPatchSchema>;

const oauthApp = (clientId: RegExp) =>
  z.object({
    clientId: z.string().trim().regex(clientId),
    // Absent = on garde le secret déjà enregistré.
    clientSecret: z.string().trim().min(5).max(500).optional(),
  });
// Un identifiant collé avec un caractère en trop (« / » final…) est refusé à l'enregistrement
// plutôt que de faire échouer la connexion chez Google ou Microsoft.
export const googleAppSchema = oauthApp(/^[0-9]+-[a-z0-9]+\.apps\.googleusercontent\.com$/);
export const microsoftAppSchema = oauthApp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);

export const eventRangeSchema = z
  .object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    /** Inclure les blocs du blocage croisé (affichés discrètement). */
    blocks: z.enum(["true", "false"]).optional(),
  })
  .refine((r) => new Date(r.to).getTime() - new Date(r.from).getTime() <= 400 * 86_400_000, "Range too long");

export interface CalendarView {
  id: string;
  connectionId: string;
  name: string;
  remoteName: string;
  color: string;
  shown: boolean;
  countsAsBusy: boolean;
  writable: boolean;
  defaultVisibility: VisibilityLevel;
  isDefault: boolean;
}

export interface ConnectionView {
  id: string;
  provider: Provider;
  label: string;
  status: "ok" | "error" | "reauth";
  lastError: string | null;
  lastSyncedAt: string | null;
  syncing: boolean;
  calendars: CalendarView[];
}

export interface EventView {
  id: string;
  calendarId: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  location: string | null;
  meetingUrl: string | null;
  status: "confirmed" | "tentative" | "cancelled";
  transparency: "opaque" | "transparent";
  description: string | null;
  attendees: EventAttendee[];
  recurring: boolean;
  isPrivate: boolean;
  declined: boolean;
  /** Fuseau d'origine de l'événement, quand le fournisseur l'indique. */
  timezone: string | null;
  /** Événement d'un agenda partagé au niveau « occupé » : seuls les horaires sont connus. */
  busy?: boolean;
  /** Bloc créé par le blocage croisé. */
  block?: boolean;
}

export interface EventAttendee {
  email: string;
  name?: string;
  status?: "accepted" | "declined" | "tentative" | "needs-action";
  organizer?: boolean;
}

export interface ProvidersStatus {
  google: boolean;
  microsoft: boolean;
}

// ───── Création et modification d'événements ─────

const dayString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const WEEKDAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const recurrenceSchema = z
  .object({
    freq: z.enum(["daily", "weekly", "monthly", "yearly"]),
    interval: z.number().int().min(1).max(99).default(1),
    byDay: z.array(z.enum(WEEKDAYS)).max(7).optional(),
    until: dayString.optional(),
    count: z.number().int().min(1).max(730).optional(),
  })
  .refine((r) => !(r.until && r.count), "Choose an end date or a number of times, not both");
export type Recurrence = z.infer<typeof recurrenceSchema>;

const eventFields = {
  title: z.string().trim().max(500),
  allDay: z.boolean(),
  /** Instant ISO pour un événement horaire, date AAAA-MM-JJ pour une journée entière (fin exclusive). */
  start: z.string().max(40),
  end: z.string().max(40),
  timezone: timezoneSchema,
  location: z.string().trim().max(1000).nullable(),
  description: z.string().max(20_000).nullable(),
  attendees: z.array(emailSchema).max(100),
  transparency: z.enum(["opaque", "transparent"]),
  isPrivate: z.boolean(),
  /** Ajoute une visio Meet (Google) ou Teams (Microsoft). */
  meeting: z.boolean(),
  recurrence: recurrenceSchema.nullable(),
  /** Rappel en minutes avant le début ; null = réglage par défaut de l'agenda. */
  reminderMinutes: z.number().int().min(0).max(40_320).nullable(),
};

type Times = { allDay?: boolean; start?: string; end?: string };
function checkTimes(e: Times, ctx: z.RefinementCtx) {
  if (e.start === undefined && e.end === undefined) return;
  if (e.start === undefined || e.end === undefined || e.allDay === undefined) {
    ctx.addIssue({ code: "custom", path: ["start"], message: "Start, end and all-day go together" });
    return;
  }
  const valid = e.allDay
    ? dayString.safeParse(e.start).success && dayString.safeParse(e.end).success
    : z.iso.datetime({ offset: true }).safeParse(e.start).success && z.iso.datetime({ offset: true }).safeParse(e.end).success;
  if (!valid) ctx.addIssue({ code: "custom", path: ["start"], message: "Invalid date" });
  else if (new Date(e.end) <= new Date(e.start)) ctx.addIssue({ code: "custom", path: ["end"], message: "The end must be after the start" });
}

export const eventCreateSchema = z
  .object({ calendarId: z.uuid(), ...eventFields })
  .superRefine(checkTimes);
export type EventCreate = z.input<typeof eventCreateSchema>;

export const EVENT_SCOPES = ["this", "following", "all"] as const;
export type EventScope = (typeof EVENT_SCOPES)[number];

/** Modification : seuls les champs envoyés changent. */
export const eventUpdateSchema = z
  .object({ ...eventFields, scope: z.enum(EVENT_SCOPES).default("this") })
  .partial()
  .superRefine(checkTimes);
export type EventUpdate = z.input<typeof eventUpdateSchema>;

export const eventDeleteSchema = z.object({ scope: z.enum(EVENT_SCOPES).default("this") });

export const placeQuerySchema = z.object({
  q: z.string().trim().min(2).max(200),
  lat: z.coerce.number().min(-90).max(90).optional(),
  lon: z.coerce.number().min(-180).max(180).optional(),
  lang: z.string().max(5).optional(),
});

export interface Place {
  name: string;
  /** Adresse lisible, sans le nom. */
  address: string;
  lat: number;
  lon: number;
  /** Catégorie OpenStreetMap (restaurant, gare…), quand elle est connue. */
  kind: string | null;
}

// ───── Partage ─────

export const shareCreateSchema = z.object({ email: emailSchema });
export const shareLevelsSchema = z.object({ levels: z.record(z.uuid(), z.enum(VISIBILITY_LEVELS)) });
export const shareViewerSchema = z
  .object({ label: z.string().trim().max(100).nullable(), color: hexColorSchema.nullable(), hidden: z.boolean() })
  .partial();
export type ShareViewerPatch = z.input<typeof shareViewerSchema>;

/** Partage vu par son propriétaire. */
export interface OwnedShareView {
  id: string;
  email: string;
  viewerName: string | null;
  /** pending : l'invité n'a pas encore de compte. */
  status: "pending" | "active";
  /** Niveaux réglés ; un agenda absent suit sa visibilité par défaut. */
  levels: Record<string, VisibilityLevel>;
}

/** Partage vu par l'invité. */
export interface ReceivedShareView {
  id: string;
  owner: { name: string; email: string };
  label: string | null;
  color: string;
  hidden: boolean;
}

// ───── Réservation ─────

export const bookingSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/, "Letters, digits and dashes only");

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/);
const timeRange = z.object({ start: hhmm, end: hhmm }).refine((r) => r.start < r.end, "The end must be after the start");
export type TimeRange = z.infer<typeof timeRange>;

export const scheduleSchema = z.object({
  name: z.string().trim().min(1).max(100),
  timezone: timezoneSchema,
  weekly: z.partialRecord(z.enum(["0", "1", "2", "3", "4", "5", "6"]), z.array(timeRange).max(6)),
  overrides: z.array(z.object({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), ranges: z.array(timeRange).max(6) })).max(200),
});
export type ScheduleInput = z.input<typeof scheduleSchema>;

export const LOCATION_KINDS = ["video", "place", "phone", "none"] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number];
export const QUESTION_TYPES = ["text", "textarea", "choice", "checkbox"] as const;

export const bookingQuestionSchema = z.object({
  id: z.string().min(1).max(40),
  label: z.string().trim().min(1).max(200),
  type: z.enum(QUESTION_TYPES),
  options: z.array(z.string().trim().min(1).max(100)).max(20).optional(),
  required: z.boolean(),
});
export type BookingQuestion = z.infer<typeof bookingQuestionSchema>;

export const bookingTypeSchema = z.object({
  slug: bookingSlugSchema,
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(2000).nullable(),
  durationMin: z.number().int().min(5).max(480),
  color: hexColorSchema,
  calendarId: z.uuid().nullable(),
  scheduleId: z.uuid(),
  locationKind: z.enum(LOCATION_KINDS),
  locationText: z.string().trim().max(500).nullable(),
  bufferBeforeMin: z.number().int().min(0).max(240),
  bufferAfterMin: z.number().int().min(0).max(240),
  minNoticeMin: z.number().int().min(0).max(43_200),
  horizonDays: z.number().int().min(1).max(365),
  intervalMin: z.number().int().min(5).max(240),
  dailyLimit: z.number().int().min(1).max(50).nullable(),
  questions: z.array(bookingQuestionSchema).max(10),
  active: z.boolean(),
});
export type BookingTypeInput = z.input<typeof bookingTypeSchema>;

export interface ScheduleView extends ScheduleInput {
  id: string;
}
export interface BookingTypeView extends z.output<typeof bookingTypeSchema> {
  id: string;
}
export interface BookingView {
  id: string;
  typeName: string;
  start: string;
  end: string;
  name: string;
  email: string;
  timezone: string;
  answers: { label: string; value: string }[];
  status: "confirmed" | "cancelled";
}

/** Page publique : types de rendez-vous actifs. */
export interface PublicTypeView {
  slug: string;
  name: string;
  description: string | null;
  durationMin: number;
  color: string;
  locationKind: LocationKind;
  /** Adresse du lieu fixe (type « place ») ; rien d'autre n'est exposé. */
  locationText: string | null;
  questions: BookingQuestion[];
  /** Fuseau des horaires du propriétaire. */
  timezone: string;
}
export interface PublicPageView {
  owner: { name: string };
  types: PublicTypeView[];
}

export const slotsQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const bookingRequestSchema = z.object({
  start: z.iso.datetime({ offset: true }),
  name: z.string().trim().min(1).max(120),
  email: emailSchema,
  timezone: timezoneSchema,
  locale: z.enum(LOCALES),
  phone: z.string().trim().max(40).optional(),
  answers: z.record(z.string().max(40), z.union([z.string().max(5000), z.boolean()])).default({}),
  /** Champ piège invisible : rempli uniquement par les robots. */
  website: z.string().max(200).optional(),
});
export type BookingRequest = z.input<typeof bookingRequestSchema>;

export const rescheduleSchema = z.object({ start: z.iso.datetime({ offset: true }) });

/** Réservation vue par le visiteur, via son lien secret. */
export interface ManageView {
  ownerName: string;
  typeName: string;
  start: string;
  end: string;
  status: "confirmed" | "cancelled";
  name: string;
  timezone: string;
  slug: string | null;
  typeSlug: string | null;
  /** Le rendez-vous est passé ou annulé : plus de modification possible. */
  locked: boolean;
}

// ───── Blocage croisé ─────

export const blockRuleSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    targetCalendarId: z.uuid(),
    sourceCalendarIds: z.array(z.uuid()).min(1).max(50),
    includeAllDay: z.boolean(),
    windowDays: z.number().int().min(7).max(365),
    active: z.boolean(),
  })
  .refine((r) => !r.sourceCalendarIds.includes(r.targetCalendarId), { message: "The target can't be a source", path: ["sourceCalendarIds"] });
export type BlockRuleInput = z.input<typeof blockRuleSchema>;

export interface BlockRuleView extends BlockRuleInput {
  id: string;
  blockCount: number;
  lastRunAt: string | null;
  lastError: string | null;
}
